/**
 * @module FieldControls
 */
import { Button, createStyles, FormControl, FormHelperText, Input, InputLabel, makeStyles, Tooltip, Typography } from '@material-ui/core';
import CloudDownload from '@material-ui/icons/CloudDownload';
import { deepMerge, PathHelper } from '@sensenet/client-utils';
import { downloadFile, useRepository } from '@sensenet/hooks-react';
import React, { useEffect, useState } from 'react';
import { defaultLocalization } from "./localization.js";
const useStyles = makeStyles(() => {
  return createStyles({
    binaryContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start'
    },
    downloadContainer: {
      cursor: 'pointer'
    },
    downloadButton: {
      minWidth: 'unset'
    },
    downloadIcon: {
      fontSize: '23px'
    },
    textDate: {
      fontSize: '0.66rem',
      letterSpacing: '0.5px',
      marginLeft: '5px',
      verticalAlign: 'middle'
    },
    root: {
      display: 'flex',
      flexWrap: 'wrap'
    },
    label: {
      padding: 0,
      fontSize: '12px',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      lineHeight: 1
    },
    value: {
      fontStyle: 'italic',
      margin: '5px 0'
    },
    inputLabel: {
      position: 'relative',
      transform: 'none'
    }
  });
});
export const errorMessages = {
  repository: 'You must pass a repository to this control.',
  contentToFetch: 'There needs to be a content to get the name of the binary field.',
  contentToUpload: 'There needs to be a content to be able to upload.'
};
/**
 * Field control that represents a FileUpload field. Available values will be populated from the FieldSettings.
 */

export const FileUpload = props => {
  var _props$localization;

  const localization = deepMerge(defaultLocalization.fileUpload, (_props$localization = props.localization) === null || _props$localization === void 0 ? void 0 : _props$localization.fileUpload);
  const repo = useRepository();
  const [fileName, setFileName] = useState('');
  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      try {
        if (!props.repository) {
          throw new Error(errorMessages.repository);
        }

        if (!props.content) {
          throw new Error(errorMessages.contentToFetch);
        }

        const loadPath = PathHelper.joinPaths(PathHelper.getContentUrl(props.content.Path), '/', props.settings.Name);
        const binaryField = await props.repository.load({
          idOrPath: loadPath,
          requestInit: {
            signal: ac.signal
          }
        });
        const binaryData = Object.values(binaryField.d)[0];
        setFileName(binaryData.FileName.FullFileName);
      } catch (error) {
        console.error(error.message);
      }
    })();

    return () => ac.abort();
  }, [props.content, props.repository, props.settings.Name]);
  /**
   * returns a name from the given path
   */

  const getNameFromPath = path => path.replace(/^.*[\\/]/, '');

  const binaryFieldValue = props.fieldValue;
  const mediaResource = binaryFieldValue === null || binaryFieldValue === void 0 ? void 0 : binaryFieldValue.__mediaresource;
  /**
   * handles change event on the fileupload input
   */

  const handleUpload = async e => {
    try {
      if (!props.repository) {
        throw new Error(errorMessages.repository);
      }

      if (!props.content) {
        throw new Error(errorMessages.contentToUpload);
      }

      e.persist();

      if (!e.target.files) {
        return;
      }

      await props.repository.upload.file({
        parentPath: PathHelper.getParentPath(props.content.Path),
        file: e.target.files[0],
        fileName: props.content.Name,
        overwrite: true,
        contentTypeName: props.content.Type,
        binaryPropertyName: props.settings.Name
      });
      const newValue = `${getNameFromPath(e.target.value)}`;
      setFileName(newValue);
    } catch (error) {
      console.error(error.message);
    }
  };

  const classes = useStyles();

  switch (props.actionName) {
    case 'edit':
    case 'new':
      return /*#__PURE__*/React.createElement(FormControl, {
        className: classes.root,
        key: props.settings.Name,
        component: 'fieldset',
        required: props.settings.Compulsory
      }, /*#__PURE__*/React.createElement("label", {
        className: classes.label,
        htmlFor: props.settings.Name
      }, props.settings.DisplayName), /*#__PURE__*/React.createElement(Typography, {
        variant: "body1",
        gutterBottom: true
      }, fileName), /*#__PURE__*/React.createElement(InputLabel, {
        className: classes.inputLabel,
        htmlFor: `raised-button-file-${props.settings.Name}`
      }, /*#__PURE__*/React.createElement(Button, {
        "aria-label": localization.buttonText,
        variant: "contained",
        component: "span",
        color: "primary"
      }, localization.buttonText)), !props.hideDescription && /*#__PURE__*/React.createElement(FormHelperText, null, props.settings.Description), /*#__PURE__*/React.createElement(Input, {
        style: {
          display: 'none'
        },
        id: `raised-button-file-${props.settings.Name}`,
        type: "file",
        onChange: handleUpload
      }));

    case 'browse':
    default:
      return /*#__PURE__*/React.createElement("div", {
        className: classes.binaryContainer
      }, /*#__PURE__*/React.createElement(Typography, {
        variant: "caption",
        gutterBottom: true
      }, props.settings.DisplayName), mediaResource !== null && mediaResource !== void 0 && mediaResource.content_type ? /*#__PURE__*/React.createElement(Tooltip, {
        title: fileName
      }, /*#__PURE__*/React.createElement(Button, {
        "data-test": "download-button",
        className: classes.downloadButton,
        onClick: () => downloadFile(mediaResource === null || mediaResource === void 0 ? void 0 : mediaResource.media_src, repo.configuration.repositoryUrl),
        "aria-label": localization.downloadButtonText,
        variant: "contained",
        component: "span",
        color: "primary"
      }, /*#__PURE__*/React.createElement(CloudDownload, {
        className: classes.downloadIcon
      }))) : /*#__PURE__*/React.createElement(Typography, {
        variant: "caption",
        gutterBottom: true
      }, localization.noValue));
  }
};
//# sourceMappingURL=file-upload.js.map